/**
 * AgentProgressUIVoiceDTMF - Complete Figma redesign (Refactored)
 *
 * Full implementation following Figma specs:
 * - Payment method selection with saved cards
 * - Secure mode OFF/ON states
 * - Progressive masked entry
 * - Success/Error screens
 * - Manual mode fallback
 *
 * Refactored to use custom hooks and utilities for better maintainability.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  AgentProgressUIVoiceDTMFProps,
  CardType,
  DTMFEvent,
} from '@/types/voice-dtmf.types';
import styles from './AgentProgressUIVoiceDTMF.module.css';

// Sub-components
import { PaymentMethodSelection } from './screens/PaymentMethodSelection';
import { CardEntry } from './screens/CardEntry';
import { Success } from './screens/Success';
import { ErrorScreen } from './screens/ErrorScreen';
import { DeletingCard } from './screens/DeletingCard';
import { WaitingManualPayment } from './screens/WaitingManualPayment';
import { DeleteCardModal } from './components/DeleteCardModal';

// Hooks
import { useVoiceDTMFSession } from './hooks/useVoiceDTMFSession';
import { useSSEConnection } from './hooks/useSSEConnection';
import { useMockSimulation } from './hooks/useMockSimulation';
import { usePaymentHandlers } from './hooks/usePaymentHandlers';
import { useAgentApi } from '@hooks/useAgentApi';
import { logger } from '@utils/logger';
import { handleSessionTermination } from '@utils/sessionTermination';

export const AgentProgressUIVoiceDTMF = ({
  sessionId,
  customerId,
  amount,
  currency,
  callReference,
  sseUrl,
  paymentId,
  useMockData = false,
  mockScenario = 'new-card-success',
  mockAutoProgress = true,
  mockPlaygroundConfig,
  onSimulateSecureModeOn,
  onSimulateSaveCard,
  onSessionComplete: _onSessionComplete,
  onSessionDeclined,
  onSessionCancel: _onSessionCancel,
  onFallbackToManual,
  onError,
  onScreenChange,
  initialSession,
  initialManualModeEnabled,
  shouldSaveCard = 'NEVER',
  partnerModeEnabled = false,
  interactionType,
  cancelButtonEnabled = false,
}: AgentProgressUIVoiceDTMFProps) => {
  // Agent API integration
  // Skip authentication when useMockData is true (Storybook/Demo/QA).
  // Do NOT gate on `mockScenario !== undefined` — mockScenario has a default value
  // ('new-card-success') so it is never undefined, which would make skip always true
  // and permanently bypass auth in production.
  const { apiClient, error: authError } = useAgentApi({
    agentSessionId: sessionId,
    skip: useMockData,
    initialSession,
  });

  // Surface auth errors (e.g. session expired between readPaymentSession and mount)
  useEffect(() => {
    if (authError) onError?.(authError);
  }, [authError, onError]);

  // Session state management
  const { sessionState, setSessionState, loading, refetch } =
    useVoiceDTMFSession({
      sessionId,
      customerId,
      amount,
      currency,
      callReference,
      paymentId,
      useMockData,
      mockScenario,
      mockAutoProgress,
      mockPlaygroundConfig,
      onError,
      apiClient: useMockData ? undefined : apiClient,
      initialManualModeEnabled,
      partnerModeEnabled,
      interactionType,
    });

  // Keep a ref to sessionState.screen so handleDTMFEvent can read the latest
  // value without being recreated every time screen changes — recreating it
  // causes useSSEConnection to reconnect on every state update.
  const sessionScreenRef = useRef(sessionState.screen);
  sessionScreenRef.current = sessionState.screen;

  // Tracks whether the checkout tab has already been opened this session mount.
  // Set synchronously before window.open so that replay events arriving in the
  // same SSE chunk (before React re-renders) are also blocked. Resets on page
  // refresh because the component remounts and useRef reinitialises to false.
  // Also passed to usePaymentHandlers so handleManualModeClick can pre-claim it
  // before the POST await, preventing the SSE handler from opening a second tab.
  const checkoutOpenedRef = useRef(false);

  // Reference to the checkout tab opened via window.open, so it can be closed
  // from handleCancel. Populated by usePaymentHandlers (manual mode click,
  // popup-blocked recovery link) and by the SSE handlers below (VOICE_MANUAL_INITIATED,
  // FALLBACK_TO_MANUAL_MODE). None of the window.open calls pass 'noopener' anymore —
  // per spec, noopener forces window.open to return null, making the reference
  // impossible to capture.
  const checkoutWindowRef = useRef<Window | null>(null);

  // Payment event handlers (pass apiClient)
  const {
    isProcessing,
    isCancelling,
    handlePaymentMethodSelect,
    handleDeleteCardClick,
    handleDeleteCardConfirm,
    handleDeleteCardCancel,
    handleResetSensitiveData,
    handleCompletePayment,
    handleManualModeClick,
    handleOpenCheckout,
    handleFieldChange,
    handleTryAgain,
    handleCancel,
  } = usePaymentHandlers({
    sessionState,
    setSessionState,
    sessionId,
    paymentId,
    amount,
    currency,
    useMockData,
    mockScenario,
    onSessionDeclined,
    onFallbackToManual,
    onError,
    refetch,
    apiClient: useMockData ? undefined : apiClient,
    interactionType,
    paymentLink: initialSession?.paymentLink,
    initialSession,
    checkoutOpenedRef,
    checkoutWindowRef,
    shouldSaveCard,
  });

  // SSE connection for real-time events (pass apiClient)
  const handleDTMFEvent = useCallback(
    (event: DTMFEvent) => {
      logger.info('DTMF Event received', event);

      switch (event.eventType) {
        case 'PAYMENT_COMPLETE': {
          if (
            sessionScreenRef.current !== 'waiting-manual-payment' &&
            sessionScreenRef.current !== 'processing'
          )
            break;
          const d =
            event.data !== null && typeof event.data === 'object'
              ? (event.data as Record<string, unknown>)
              : {};
          const orderInfo =
            d.orderInfo !== null && typeof d.orderInfo === 'object'
              ? (d.orderInfo as Record<string, unknown>)
              : {};
          const ssePaymentId =
            typeof d.paymentId === 'string' ? d.paymentId : undefined;
          const merchantTransactionId =
            initialSession?.merchantTransactionId ??
            (typeof d.transactionId === 'string' ? d.transactionId : undefined);

          logger.info('Payment completed', event.data);

          setSessionState((prev) => ({
            ...prev,
            screen: 'success',
            paymentStatus: 'completed',
            orderInfo: {
              btPayId: ssePaymentId ?? customerId,
              merchantTransactionId,
              amount:
                typeof orderInfo.amount === 'number'
                  ? orderInfo.amount
                  : amount,
              currency:
                typeof orderInfo.currency === 'string'
                  ? orderInfo.currency
                  : currency,
              maskedCardNumber:
                typeof d.maskedCardNumber === 'string'
                  ? d.maskedCardNumber
                  : '****',
              cardType: typeof d.cardType === 'string' ? d.cardType : 'MANUAL',
              expiryDate: typeof d.expiryDate === 'string' ? d.expiryDate : '',
              timestamp: new Date().toISOString(),
            },
          }));
          break;
        }

        case 'SESSION_UPDATED': {
          const d =
            event.data !== null && typeof event.data === 'object'
              ? (event.data as Record<string, unknown>)
              : {};
          const callReference =
            typeof d.callReference === 'string' ? d.callReference : undefined;
          setSessionState((prev) => ({
            ...prev,
            secureMode: {
              ...prev.secureMode,
              // Only force mode to 'off' when already in partner mode (mode was 'off').
              // In non-partner mode secure mode is already 'on' — don't downgrade it.
              mode:
                prev.secureMode.mode === 'off' ? 'off' : prev.secureMode.mode,
              callCode: callReference,
            },
          }));
          logger.info('Call Reference updated via SSE', {
            sessionId,
            callReference,
          });
          break;
        }

        case 'SESSION_EXPIRED': {
          // Backend-authoritative expiry via SSE. Mirrors the client-side timer
          // guard: do not overwrite a terminal screen (success/error).
          setSessionState((prev) => {
            if (prev.screen === 'success' || prev.screen === 'error')
              return prev;
            return { ...prev, screen: 'error', errorType: 'session-expired' };
          });
          logger.warn('Session expired (SSE event)', { sessionId });
          break;
        }

        case 'SECURE_MODE_ENTERED': {
          const d =
            event.data !== null && typeof event.data === 'object'
              ? (event.data as Record<string, unknown>)
              : {};
          const callReference =
            typeof d.callReference === 'string' ? d.callReference : undefined;
          setSessionState((prev) => {
            // Guard: never overwrite a terminal screen with a delayed SSE event
            if (prev.screen === 'success' || prev.screen === 'error')
              return prev;
            return {
              ...prev,
              screen: 'card-entry',
              secureMode: {
                ...prev.secureMode,
                mode: 'on',
                callCode: callReference,
              },
            };
          });
          logger.info('Customer entered CR - transitioning to secure mode', {
            sessionId,
            callReference,
          });
          break;
        }

        case 'SESSION_FAILED': {
          const d =
            event.data !== null && typeof event.data === 'object'
              ? (event.data as Record<string, unknown>)
              : {};
          const errorCode =
            typeof d.errorCode === 'string' ? d.errorCode : undefined;
          const errorMessage =
            typeof d.errorMessage === 'string' ? d.errorMessage : undefined;
          const reason = typeof d.reason === 'string' ? d.reason : undefined;

          // Two emitters share SESSION_FAILED with different payloads:
          // - Partner Mode CR generation: { errorCode, errorMessage }
          // - Direct VOICE_MANUAL trigger: { reason } (raw exception message)
          // Distinguish by payload shape. Never surface the raw backend text to
          // the agent — log it for diagnostics and show a fixed friendly message.
          const isVoiceManualFailure = reason !== undefined && !errorMessage;
          const friendlyMessage = isVoiceManualFailure
            ? "We couldn't start the manual payment. Please try again."
            : 'Failed to generate Call Reference';

          logger.error('SESSION_FAILED received', {
            sessionId,
            errorCode,
            errorMessage,
            reason,
          });

          setSessionState((prev) => ({
            ...prev,
            screen: 'error',
            // SESSION_FAILED maps to retry-later for both emitters: the agent can
            // retry after a Partner Mode CR failure or a VOICE_MANUAL init failure.
            // Differentiate the error type here if future error codes require it.
            errorType: 'retry-later',
            errorMessage: friendlyMessage,
          }));
          break;
        }

        case 'VOICE_MANUAL_INITIATED': {
          const d =
            event.data !== null && typeof event.data === 'object'
              ? (event.data as Record<string, unknown>)
              : {};
          const checkoutUrl =
            typeof d.checkoutUrl === 'string' ? d.checkoutUrl : undefined;
          if (!checkoutUrl) {
            logger.warn('VOICE_MANUAL_INITIATED received without checkoutUrl', {
              sessionId,
            });
            break;
          }
          if (
            checkoutOpenedRef.current ||
            sessionScreenRef.current === 'success' ||
            sessionScreenRef.current === 'error'
          )
            break;
          checkoutOpenedRef.current = true;
          const newWindow = window.open(checkoutUrl, '_blank');
          checkoutWindowRef.current = newWindow;
          setSessionState((prev) => {
            if (prev.screen === 'success' || prev.screen === 'error')
              return prev;
            return {
              ...prev,
              screen: 'waiting-manual-payment',
              wasManualPayment: true,
              popupBlockedUrl: checkoutUrl,
              popupBlocked: newWindow === null,
            };
          });
          logger.info('Direct VOICE_MANUAL initiated', { sessionId });
          break;
        }

        case 'FALLBACK_TO_MANUAL_MODE': {
          const d =
            event.data !== null && typeof event.data === 'object'
              ? (event.data as Record<string, unknown>)
              : {};
          const checkoutUrl =
            typeof d.checkoutUrl === 'string' ? d.checkoutUrl : undefined;
          const reason = typeof d.reason === 'string' ? d.reason : undefined;
          if (!checkoutUrl) {
            logger.warn(
              'FALLBACK_TO_MANUAL_MODE received without checkoutUrl',
              {
                sessionId,
                reason,
              }
            );
            break;
          }
          if (
            checkoutOpenedRef.current ||
            sessionScreenRef.current === 'success' ||
            sessionScreenRef.current === 'error' ||
            sessionScreenRef.current === 'waiting-manual-payment'
          )
            break;
          checkoutOpenedRef.current = true;
          const newWindow = window.open(checkoutUrl, '_blank');
          checkoutWindowRef.current = newWindow;
          setSessionState((prev) => {
            if (prev.screen === 'success' || prev.screen === 'error')
              return prev;
            return {
              ...prev,
              screen: 'waiting-manual-payment',
              wasManualPayment: true,
              popupBlockedUrl: checkoutUrl,
              popupBlocked: newWindow === null,
            };
          });
          logger.info('Auto-fallback to manual mode triggered', {
            sessionId,
            reason,
          });
          break;
        }

        case 'FIELD_COMPLETE': {
          const d =
            event.data !== null && typeof event.data === 'object'
              ? (event.data as Record<string, unknown>)
              : {};
          const field = typeof d.field === 'string' ? d.field : undefined;
          const maskedValue =
            typeof d.maskedValue === 'string' ? d.maskedValue : undefined;
          const digitsCollected =
            typeof d.digitsCollected === 'number'
              ? d.digitsCollected
              : undefined;
          const totalDigits =
            typeof d.totalDigits === 'number' ? d.totalDigits : undefined;
          const isComplete =
            typeof d.isComplete === 'boolean' ? d.isComplete : false;
          const VALID_CARD_TYPES: CardType[] = [
            'VISA',
            'MASTERCARD',
            'AMEX',
            'UNKNOWN',
          ];
          const cardTypeRaw =
            typeof d.cardType === 'string' ? d.cardType : undefined;
          const cardType: CardType | undefined =
            cardTypeRaw && VALID_CARD_TYPES.includes(cardTypeRaw as CardType)
              ? (cardTypeRaw as CardType)
              : cardTypeRaw
                ? 'UNKNOWN'
                : undefined;

          if (!field) {
            logger.warn('FIELD_COMPLETE received without field', { sessionId });
            break;
          }

          setSessionState((prev) => {
            if (!prev.cardEntry) return prev;
            if (field === 'CARD_NUMBER') {
              // Field is already complete and valid — discard late FIELD_PROGRESS events.
              // Only reset when handleResetSensitiveData sets isComplete back to false.
              if (prev.cardEntry.cardNumber.isComplete && !isComplete)
                return prev;
              const newDigitsEntered =
                digitsCollected ?? prev.cardEntry.cardNumber.digitsEntered;
              const newTotalDigits =
                totalDigits ?? prev.cardEntry.cardNumber.totalDigits;
              // All digits received but not yet confirmed valid by backend — show
              // invalid UI immediately so the agent gets instant feedback.
              const allDigitsEntered =
                !isComplete &&
                typeof newDigitsEntered === 'number' &&
                typeof newTotalDigits === 'number' &&
                newDigitsEntered > 0 &&
                newDigitsEntered >= newTotalDigits;
              return {
                ...prev,
                cardEntry: {
                  ...prev.cardEntry,
                  cardNumber: {
                    ...prev.cardEntry.cardNumber,
                    value: maskedValue ?? prev.cardEntry.cardNumber.value,
                    isComplete,
                    // Only clear the invalid flag when this is a genuine
                    // FIELD_COLLECTED(VALID) event (isComplete === true) AND new digits
                    // have been entered since the last FIELD_INVALID (digitsEntered > 0).
                    // FIELD_INVALID resets digitsEntered to 0, so a FIELD_COLLECTED(VALID)
                    // arriving before any new input (digitsEntered still 0) is spurious —
                    // the backend emits VALID/INVALID pairs in quick succession; without
                    // this guard the spurious VALID clears isInvalid and the error flickers.
                    ...(isComplete &&
                    (!prev.cardEntry.cardNumber.isInvalid ||
                      (prev.cardEntry.cardNumber.digitsEntered ?? 0) > 0)
                      ? { isInvalid: false, pendingValidation: false }
                      : {}),
                    pendingValidation: isComplete ? false : allDigitsEntered,
                    digitsEntered: newDigitsEntered,
                    totalDigits: newTotalDigits,
                  },
                  ...(cardType
                    ? { cardType }
                    : isComplete && !prev.cardEntry.cardType
                      ? { cardType: 'UNKNOWN' as CardType }
                      : {}),
                },
              };
            }
            if (field === 'CVC') {
              // Same guard as CARD_NUMBER — discard late FIELD_PROGRESS once complete.
              if (prev.cardEntry.cvv.isComplete && !isComplete) return prev;
              const newDigitsEntered =
                digitsCollected ?? prev.cardEntry.cvv.digitsEntered;
              const newTotalDigits =
                totalDigits ?? prev.cardEntry.cvv.totalDigits;
              const allDigitsEntered =
                !isComplete &&
                typeof newDigitsEntered === 'number' &&
                typeof newTotalDigits === 'number' &&
                newDigitsEntered > 0 &&
                newDigitsEntered >= newTotalDigits;
              return {
                ...prev,
                cardEntry: {
                  ...prev.cardEntry,
                  cvv: {
                    ...prev.cardEntry.cvv,
                    value: maskedValue ?? prev.cardEntry.cvv.value,
                    isComplete,
                    // Same guard as CARD_NUMBER above.
                    ...(isComplete &&
                    (!prev.cardEntry.cvv.isInvalid ||
                      (prev.cardEntry.cvv.digitsEntered ?? 0) > 0)
                      ? { isInvalid: false, pendingValidation: false }
                      : {}),
                    pendingValidation: isComplete ? false : allDigitsEntered,
                    digitsEntered: newDigitsEntered,
                    totalDigits: newTotalDigits,
                  },
                },
              };
            }
            return prev;
          });
          logger.info('Field collected via SSE', {
            sessionId,
            field,
            isComplete,
          });
          break;
        }

        case 'FIELD_INVALID': {
          const d =
            event.data !== null && typeof event.data === 'object'
              ? (event.data as Record<string, unknown>)
              : {};
          const field = typeof d.field === 'string' ? d.field : undefined;
          if (!field) {
            logger.warn('FIELD_INVALID received without field', { sessionId });
            break;
          }
          setSessionState((prev) => {
            if (prev.screen === 'success' || prev.screen === 'error')
              return prev;
            if (!prev.cardEntry) return prev;
            const invalidFieldBase = {
              isComplete: false,
              isInvalid: true,
              pendingValidation: false,
              digitsEntered: 0,
              totalDigits: 0,
            };
            if (field === 'CARD_NUMBER') {
              return {
                ...prev,
                cardEntry: {
                  ...prev.cardEntry,
                  cardNumber: {
                    ...invalidFieldBase,
                    value: prev.cardEntry.cardNumber.value,
                  },
                },
              };
            }
            if (field === 'CVC') {
              return {
                ...prev,
                cardEntry: {
                  ...prev.cardEntry,
                  cvv: {
                    ...invalidFieldBase,
                    value: prev.cardEntry.cvv.value,
                  },
                },
              };
            }
            return prev;
          });
          logger.info('Field invalid — marked invalid, value preserved', {
            sessionId,
            field,
          });
          break;
        }

        case 'VOICE_MANUAL_RECOMMENDED': {
          setSessionState((prev) => {
            if (prev.screen === 'success' || prev.screen === 'error')
              return prev;
            return { ...prev, manualModeAvailable: true };
          });
          logger.info('Voice manual recommended — surfacing manual mode', {
            sessionId,
          });
          break;
        }

        case 'PAYMENT_DECLINED': {
          if (
            sessionScreenRef.current !== 'processing' &&
            sessionScreenRef.current !== 'waiting-manual-payment'
          )
            break;
          const pd =
            event.data !== null && typeof event.data === 'object'
              ? (event.data as Record<string, unknown>)
              : {};
          const pdErrorCode =
            typeof pd.errorCode === 'string' ? pd.errorCode : undefined;
          if (pdErrorCode === 'MAX_ATTEMPTS_EXCEEDED') {
            setSessionState((prev) => {
              if (prev.screen === 'success' || prev.screen === 'error')
                return prev;
              return { ...prev, screen: 'error', errorType: 'max-attempts' };
            });
            onSessionDeclined?.(initialSession?.paymentId ?? sessionId);
            break;
          }
          if (pdErrorCode === 'CARD_DECLINED') {
            break;
          }
          setSessionState((prev) => {
            if (prev.screen === 'success' || prev.screen === 'error')
              return prev;
            return {
              ...prev,
              screen: 'error',
              errorType: 'dont-retry',
              errorMessage: 'Payment was declined.',
            };
          });
          break;
        }

        default:
          break;
      }
    },
    [
      setSessionState,
      sessionId,
      customerId,
      amount,
      currency,
      initialSession?.paymentId,
      initialSession?.merchantTransactionId,
      onSessionDeclined,
    ]
  );

  // Session expiry timer — driven by expiresAt from the Lambda BFF payload.
  // The backend does not guarantee a SESSION_EXPIRED SSE event, so we set our
  // own timer and transition to an error screen when the TTL is reached.
  useEffect(() => {
    if (useMockData || !initialSession?.expiresAt) return;

    const delayMs = new Date(initialSession.expiresAt).getTime() - Date.now();

    if (delayMs <= 0) {
      setSessionState((prev) => {
        if (prev.screen === 'success' || prev.screen === 'error') return prev;
        return { ...prev, screen: 'error', errorType: 'session-expired' };
      });
      return;
    }

    const timerId = window.setTimeout(() => {
      setSessionState((prev) => {
        if (prev.screen === 'success' || prev.screen === 'error') return prev;
        return { ...prev, screen: 'error', errorType: 'session-expired' };
      });
      logger.warn('Session expired (client-side timer)', { sessionId });
    }, delayMs);

    return () => window.clearTimeout(timerId);
  }, [
    useMockData,
    initialSession?.expiresAt,
    initialSession?.agentReturnUrl,
    initialSession?.paymentId,
    sessionId,
    setSessionState,
  ]);

  const [sseDisconnected, setSseDisconnected] = useState(false);

  const handleSSEConnectionError = useCallback(() => {
    setSseDisconnected(true);
    setSessionState((prev) => {
      if (
        prev.screen === 'success' ||
        prev.screen === 'error' ||
        // Only suppress the error screen if the checkout tab was already opened.
        // If SSE fails before VOICE_MANUAL_INITIATED arrives (tab never opened),
        // the error screen must show so the agent is not left with an infinite spinner.
        (prev.screen === 'waiting-manual-payment' && checkoutOpenedRef.current)
      )
        return prev;
      return { ...prev, screen: 'error', errorType: 'sse-disconnected' };
    });
  }, [setSessionState]);

  const handleSessionExpiredClose = useCallback(() => {
    handleSessionTermination({
      agentReturnUrl: initialSession?.agentReturnUrl,
      paymentId: initialSession?.paymentId ?? sessionId,
      status: 'SESSION_EXPIRED',
      sessionId,
    });
  }, [initialSession?.agentReturnUrl, initialSession?.paymentId, sessionId]);

  useSSEConnection({
    isTerminalScreen:
      sessionState.screen === 'success' || sessionState.screen === 'error',
    sseUrl,
    useMockData,
    onDTMFEvent: handleDTMFEvent,
    onConnectionError: handleSSEConnectionError,
    apiClient: useMockData ? undefined : apiClient,
  });

  // Mock simulation API (for QA testing)
  const { simulationAPI } = useMockSimulation({
    useMockData,
    mockAutoProgress,
    setSessionState,
    onSimulationReady: undefined,
  });

  // Expose simulation API via callbacks (alternative to window globals)
  useEffect(() => {
    const isPartnerMode =
      mockScenario === 'partner-mode' || mockScenario === 'partner-mode-demo';
    if (useMockData && (!mockAutoProgress || isPartnerMode)) {
      if (typeof window !== 'undefined') {
        const cleanup = {
          __simulateSecureModeOn: simulationAPI.simulateSecureModeOn,
          __simulateCustomCardNumber: simulationAPI.simulateCustomCardNumber,
          __simulateCustomCVV: simulationAPI.simulateCustomCVV,
          __simulateCustomExpiry: simulationAPI.simulateCustomExpiry,
          __simulateDTMFEvent: handleDTMFEvent,
        };

        Object.assign(window, cleanup);

        return () => {
          Object.keys(cleanup).forEach((key) => {
            delete (window as never)[key];
          });
        };
      }
    }

    // In dev mode (non-mock), expose __simulateDTMFEvent for manual SSE testing via browser console
    if (!useMockData && import.meta.env.DEV && typeof window !== 'undefined') {
      (window as never as Record<string, unknown>).__simulateDTMFEvent =
        handleDTMFEvent;
      return () => {
        delete (window as never as Record<string, unknown>).__simulateDTMFEvent;
      };
    }
  }, [
    useMockData,
    mockAutoProgress,
    mockScenario,
    simulationAPI,
    handleDTMFEvent,
    onSimulateSecureModeOn,
    onSimulateSaveCard,
  ]);

  // Notify parent of screen changes
  useEffect(() => {
    onScreenChange?.(sessionState.screen);
  }, [sessionState.screen, onScreenChange]);

  // Notify parent when payment completes so it can redirect to agentReturnUrl
  useEffect(() => {
    if (sessionState.screen === 'success' && sessionState.orderInfo) {
      _onSessionComplete?.(sessionState.orderInfo);
    }
  }, [sessionState.screen, sessionState.orderInfo, _onSessionComplete]);

  // Get card to delete from session state
  const cardToDelete = sessionState.savedCards?.find(
    (c) => c.cardId === sessionState.cardToDeleteId
  );

  const cancelDisabled =
    sessionState.screen === 'success' ||
    sessionState.screen === 'error' ||
    sessionState.screen === 'deleting-card';

  const showCancelButton = cancelButtonEnabled === true;

  // Loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  // Render appropriate screen
  return (
    <div className={styles.container}>
      {sessionState.screen === 'deleting-card' && sessionState.deletingCard && (
        <DeletingCard cardName={sessionState.deletingCard.cardName} />
      )}

      {sessionState.screen === 'payment-method-selection' && (
        <PaymentMethodSelection
          amount={amount}
          currency={currency}
          savedCards={sessionState.savedCards}
          onSelectPaymentMethod={handlePaymentMethodSelect}
          onDeleteCard={handleDeleteCardClick}
          isProcessing={isProcessing}
          onCancel={showCancelButton ? handleCancel : undefined}
          cancelDisabled={cancelDisabled}
        />
      )}

      {sessionState.screen === 'card-entry' && (
        <CardEntry
          amount={amount}
          currency={currency}
          secureMode={sessionState.secureMode}
          cardEntry={sessionState.cardEntry}
          selectedPaymentMethod={sessionState.selectedPaymentMethod}
          manualModeAvailable={sessionState.manualModeAvailable}
          isProcessing={isProcessing}
          shouldSaveCard={shouldSaveCard}
          onFieldChange={handleFieldChange}
          onRestart={handleResetSensitiveData}
          onCompletePayment={handleCompletePayment}
          onManualModeClick={handleManualModeClick}
          onCancel={showCancelButton ? handleCancel : undefined}
          cancelDisabled={cancelDisabled}
          isCancelling={isCancelling}
        />
      )}

      {sessionState.screen === 'success' &&
        sessionState.orderInfo &&
        !initialSession?.agentReturnUrl &&
        initialSession?.resultPageEnabled !== false && (
          <Success
            orderInfo={sessionState.orderInfo}
            showPaymentMethod={
              interactionType !== 'VOICE_MANUAL' &&
              !sessionState.wasManualPayment
            }
          />
        )}

      {sessionState.screen === 'error' && sessionState.errorType && (
        <ErrorScreen
          errorType={sessionState.errorType}
          errorMessage={sessionState.errorMessage}
          onTryAgain={handleTryAgain}
          onAction={
            sessionState.errorType === 'session-expired'
              ? handleSessionExpiredClose
              : sessionState.errorType === 'sse-disconnected'
                ? () => window.location.reload()
                : sessionState.errorType === 'max-attempts'
                  ? handleCancel
                  : undefined
          }
        />
      )}

      {sessionState.screen === 'waiting-manual-payment' && (
        <WaitingManualPayment
          popupBlockedUrl={sessionState.popupBlockedUrl}
          popupBlocked={sessionState.popupBlocked}
          sseDisconnected={sseDisconnected}
          onRefresh={() => {
            setSseDisconnected(false);
            refetch();
          }}
          onOpenCheckout={handleOpenCheckout}
          onCancel={showCancelButton ? handleCancel : undefined}
        />
      )}

      {/* Delete Card Modal */}
      {sessionState.showDeleteModal && cardToDelete && (
        <DeleteCardModal
          card={cardToDelete}
          onConfirm={handleDeleteCardConfirm}
          onCancel={handleDeleteCardCancel}
        />
      )}
    </div>
  );
};
